// Quick-connect identities offered by a Home (spec 295; the client half of the Home's persona registry).
//
// WHAT THIS IS FOR. A Home MAY offer a small set of pre-custodied identities a relying app can connect
// as without running a credential ceremony — the same person, same Smart Agent, same vault and org
// memberships, in every app that asks. It exists so a demo or a test can exercise a real authority
// chain instead of a mock: the identity is an actual on-chain SA whose custodian the Home holds, so
// every downstream signature, delegation and vault read is genuine.
//
// GATED BY THE HOME, NOT BY THE APP. `listQuickConnect` returns `[]` when the Home offers none, and an
// app is expected to render nothing in that case. That is the whole configuration story: a Home without
// the feature enabled reports an empty list, so the affordance disappears everywhere at once without
// any app shipping a flag. An app that hard-coded its own list would defeat that.
//
// THIS IS NOT A CREDENTIAL PATH. The app never holds a key. It asks the Home for a session and receives
// exactly what an ordinary OIDC sign-in returns — an `id_token` and the site delegation. Anyone who can
// reach an enabled Home can connect as these identities; that is what "connect as this account" means,
// and it is why a Home only enables it for accounts that exist to be shared.
//
// NO HOSTNAMES (ADR-0021): the caller supplies its Home origin, as with everything else in this package.
const trimOrigin = (o) => o.replace(/\/$/, '');
/**
 * List the identities this Home offers.
 *
 * Returns `[]` for every non-success — a Home that does not offer the feature, an origin that is not a
 * Home, a network failure. That is deliberate: the ONLY use of this list is deciding whether to show an
 * affordance, and "show nothing" is the correct outcome in all of those cases. It never throws, so a
 * Home being unreachable cannot break an app's header.
 */
export async function listQuickConnect(config) {
    const doFetch = config.fetchImpl ?? fetch;
    try {
        const res = await doFetch(`${trimOrigin(config.homeOrigin)}/connect/demo-personas`, {
            method: 'GET',
            headers: { accept: 'application/json' },
        });
        if (!res.ok)
            return [];
        const body = (await res.json());
        return Array.isArray(body.personas) ? body.personas : [];
    }
    catch {
        return [];
    }
}
/**
 * Connect as one of them.
 *
 * THROWS on failure, unlike the list above, and the asymmetry is intentional: listing decides whether to
 * offer a choice, where silence is a fine answer; connecting is a user action, where silence would leave
 * someone looking at a button that did nothing. A failed connect must say so.
 */
export async function connectAsQuickConnect(config, handleOrSa) {
    const doFetch = config.fetchImpl ?? fetch;
    const isSa = /^0x[0-9a-fA-F]{40}$/.test(handleOrSa);
    const res = await doFetch(`${trimOrigin(config.homeOrigin)}/connect/demo-signin`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...(isSa ? { sa: handleOrSa } : { handle: handleOrSa }), client_id: config.clientId }),
    });
    const body = (await res.json().catch(() => ({})));
    if (!res.ok) {
        throw new Error(body.error ?? `quick connect failed (${res.status})`);
    }
    if (typeof body.id_token !== 'string' || !body.delegation) {
        // A session without BOTH halves is not a session. The token authenticates and the delegation
        // authorizes; an app that accepted one without the other would be authenticated and unable to act,
        // or acting without having authenticated — and would discover it much later (ADR-0041).
        throw new Error('quick connect returned an incomplete session (missing id_token or delegation)');
    }
    return {
        sub: String(body.sub ?? ''),
        agentName: body.agent_name ?? null,
        idToken: body.id_token,
        delegation: body.delegation,
        homeSession: typeof body.homeSession === 'string' ? body.homeSession : undefined,
        homeSessionExpiresIn: typeof body.homeSessionExpiresIn === 'number' ? body.homeSessionExpiresIn : undefined,
    };
}
//# sourceMappingURL=quick-connect.js.map